import { useMemo } from 'react';
import type { Pageable, Page } from '../../core/api/spring-types';

/**
 * Universal Spring Report Hook
 *
 * Provides a standardized interface for all paginated reports in the application.
 * Handles state management, pagination, filtering, and sorting for Spring Data Page responses.
 *
 * This hook eliminates 50+ lines of manual adapter code per report page by providing
 * a consistent interface that works with both React Query Kit and legacy patterns.
 *
 * @template TData - The type of data items in the report
 * @template TFilter - The type of filter object
 *
 * @param queryResult - The React Query result object (must have .data, .isLoading, .isFetching, .refetch)
 * @param initialPageable - Initial pagination settings (default: page 0, size 20)
 *
 * @returns Complete report interface with data, pagination, filtering, and sorting
 *
 * @example
 * ```tsx
 * // With React Query Kit
 * const query = api.schools.report.useQuery({ variables: { filter, pageable } });
 * const report = useSpringReport(query, { page: 0, size: 20, sort: ['name,asc'] });
 *
 * // Usage in component
 * <DataTable
 *   data={report.data}
 *   onSort={report.changeSort}
 *   onFilter={(field, value) => report.updateFilter({ [field]: value })}
 * />
 * ```
 */
export interface SpringReportInterface<TData, TFilter> {
    // Data
    data: TData[];
    page: Page<TData> | undefined;

    // Loading states
    isLoading: boolean;
    isFetching: boolean;
    loading: boolean; // Combined: isLoading || isFetching

    /** Interogarea a esuat. Vezi nota din `queryResult` de ce e OPTIONAL acolo. */
    isError: boolean;

    /**
     * Interogarea NU s-a incheiat inca — porneste si de la „dezactivata pentru ca
     * o precerinta e in curs". Distinct de `isLoading`: in React Query v5 o
     * interogare dezactivata are `isPending: true` si `isFetching: false`, deci
     * `isLoading` iese FALS. Exact acolo se nastea minciuna: fara nicio incarcare
     * aparenta, raportul afisa „TOTAL … 0".
     */
    isPending: boolean;

    /**
     * Reface TOATE interogarile de care depinde raportul, nu doar pe cea proprie.
     * Optional: il pune `use-report-page`, care stie si de interogarea de editii.
     * Cine foloseste `useSpringReport` direct ramane cu `refetch`.
     */
    retry?: () => void;

    // Pagination
    currentPage: number;
    pageSize: number;
    totalPages: number;
    totalElements: number;

    // Pagination controls
    goToPage: (page: number) => void;
    changePageSize: (size: number) => void;

    // Sorting
    currentSort: string | null;
    sortField: string | null;
    sortDirection: 'asc' | 'desc' | null;
    changeSort: (field: string, direction: 'asc' | 'desc') => void;

    // Filtering
    filter: TFilter;
    updateFilter: (newFilter: Partial<TFilter>) => void;
    resetFilters: () => void;

    // Refresh
    refetch: () => void;
}

interface UseSpringReportOptions<TData, TFilter> {
    /**
     * The React Query result object
     * Must have: data (Page<TData>), isLoading, isFetching, refetch
     */
    queryResult: {
        data: Page<TData> | undefined;
        isLoading: boolean;
        isFetching: boolean;
        refetch: () => void;
        /** Vezi nota de mai jos: si asta e optional, din acelasi motiv. */
        isPending?: boolean;
        /**
         * OPTIONAL deliberat: contractul era de patru campuri, iar apelantii
         * existenti trec obiectul React Query intreg, care are deja `isError`.
         * Ca obligatoriu ar rupe orice apelant care construieste obiectul de mana,
         * fara sa castige nimic — un `queryResult` fara `isError` inseamna „nu
         * stiu daca a esuat", si se citeste `false`.
         */
        isError?: boolean;
    };

    /**
     * Initial pagination settings
     * @default { page: 0, size: 20, sort: [] }
     */
    initialPageable?: Pageable;

    /**
     * Filter state and setter (external state management)
     */
    filterState: [TFilter, React.Dispatch<React.SetStateAction<TFilter>>];

    /**
     * Pageable state and setter (external state management)
     */
    pageableState: [Pageable, React.Dispatch<React.SetStateAction<Pageable>>];
}

/**
 * Core hook implementation
 */
export function useSpringReport<TData, TFilter extends Record<string, any>>({
    queryResult,
    filterState,
    pageableState,
}: UseSpringReportOptions<TData, TFilter>): SpringReportInterface<TData, TFilter> {
    const [filter, setFilter] = filterState;
    const [pageable, setPageable] = pageableState;

    // Parse current sort
    const sortInfo = useMemo(() => {
        const sortStr = pageable.sort?.[0];
        if (!sortStr) {
            return { field: null, direction: null as 'asc' | 'desc' | null };
        }

        const [field, direction] = sortStr.split(',');
        return {
            field: field || null,
            direction: (direction as 'asc' | 'desc') || null,
        };
    }, [pageable.sort]);

    // Create the complete interface
    return useMemo(
        () => ({
            // Data
            data: queryResult.data?.content || [],
            page: queryResult.data,

            // Loading states
            isLoading: queryResult.isLoading,
            isFetching: queryResult.isFetching,
            loading: queryResult.isLoading || queryResult.isFetching,
            isError: queryResult.isError === true,
            isPending: queryResult.isPending === true,

            // Pagination
            currentPage: pageable.page ?? 0,
            pageSize: pageable.size ?? 20,
            totalPages: queryResult.data?.totalPages || 0,
            totalElements: queryResult.data?.totalElements || 0,

            // Pagination controls
            goToPage: (page: number) => setPageable((p) => ({ ...p, page })),
            changePageSize: (size: number) =>
                setPageable((p) => ({ ...p, size, page: 0 })),

            // Sorting
            currentSort: pageable.sort?.[0] || null,
            sortField: sortInfo.field,
            sortDirection: sortInfo.direction,
            changeSort: (field: string, direction: 'asc' | 'desc') =>
                setPageable((p) => ({ ...p, sort: [`${field},${direction}`], page: 0 })),

            // Filtering
            filter,
            updateFilter: (newFilter: Partial<TFilter>) => {
                setFilter((prev) => ({ ...prev, ...newFilter }));
                setPageable((p) => ({ ...p, page: 0 }));
            },
            resetFilters: () => {
                setFilter({} as TFilter);
                setPageable((p) => ({ ...p, page: 0 }));
            },

            // Refresh
            refetch: queryResult.refetch,
        }),
        [queryResult, pageable, filter, sortInfo, setFilter, setPageable]
    );
}
